import * as React from 'react';

import {colors} from 'shells/colors/colors';
import type {Props as TimelineCommentsProps} from 'shells/timeline/entry/comments/timeline-comments';
import type {Props as EmailEngagementProps} from 'shells/timeline/entry/email-engagement-reaction-bar/email-engagement';
import type {Props as ThreadDataProps} from 'shells/timeline/entry/email-engagement-reaction-bar/thread-data';
import {TimelineHeader} from 'shells/timeline/entry/header/timeline-header';
import type {Props as TimelineHeaderProps} from 'shells/timeline/entry/header/timeline-header';
import type {ToolbarOption} from 'shells/timeline/entry/toolbar/timeline-entry-toolbar';
import {TimelineEntryIcon} from 'shells/timeline/gutter/timeline-entry-icon';
import type {TimelineEntryIcon as TimelineEntryIconType} from 'shells/timeline/gutter/types';
import type {Reactions} from 'shells/timeline/reaction-bar/types';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import type {
    ReactionFragment as Reaction,
    SessionUser,
    CommentFragment as Comment,
    ReactionType,
    ChannelStatus,
    ThreadType,
} from 'nutshell-graphql-types';

import {getParticipantsForAvatarList} from '../helpers';
import {
    getCommentsWithFilteredReactions,
    getFilteredReactions,
    getToggleAction,
} from '../reactions/helpers';
import {useToggleReaction} from '../reactions/reaction-mutations';
import {TimelineEntryContext} from '../timeline';

import {TimelineEntryCard} from './timeline-entry-card';
import type {Props as TimelineEntryContentProps} from './timeline-entry-content';

import styles from './timeline-entry.module.css';

type Props = {
    id: string;
    icon: TimelineEntryIconType;
} & TimelineHeaderProps & // Header props // Content props
    TimelineEntryContentProps & // Comment props
    TimelineCommentsProps & {
        comments?: Comment[];
        reactions?: Reaction[];
        currentUser?: SessionUser;
        customBorderProps?: {
            hasDottedBorder?: boolean;
            borderColor?: string;
        };
        emailEngagement?: EmailEngagementProps;
        threadData?: ThreadDataProps;
        disableComments?: boolean;
        disableReactions?: boolean;
        actionMenu?: React.ReactNode;
        additionalToolbarOptions?: ToolbarOption[];
        onClickOpen?: (e: React.SyntheticEvent<any>) => void;
        isLastEntry?: boolean;
        isTimelineSmall?: boolean;
        hasGutterLine?: boolean;
        isPinnedThread?: boolean;
        hasActivityTranscription?: boolean;
        externalChannelStatus?: ChannelStatus | null | undefined;
        threadType?: ThreadType;
        isDashboard?: boolean;
    };

export function TimelineEntry(props: Props) {
    const {
        id,
        icon,
        entryType,
        changeType,
        timestamp,
        actor,
        subject,
        subjectTitle,
        content,
        truncatedContent,
        status,
        entity,
        participants,
        relatedLeads,
        metadata,
        reactions,
        comments,
        onAddComment,
        onDeleteComment,
        currentUser,
        customBorderProps,
        emailEngagement,
        threadData,
        disableComments,
        disableReactions,
        actionMenu,
        additionalToolbarOptions,
        onClickOpen,
        isExpanded,
        pinnedInfo,
        numVisits,
        hasGutterLine,
        externalMeetingTranscriptSummary,
        isPinnedThread,
        threadType,
        hasActivityTranscription,
        isDashboard,
    } = props;

    const timelineEntryRef = React.useRef();
    // TODO: Need to look into why this is needed for TimelineIcon resizing to work...
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [height] = useResizeObserver(timelineEntryRef.current);
    const toggleReaction = useToggleReaction();

    // Get participants for avatar list for non-note entries
    const participantsForAvatarList =
        entryType !== 'Note'
            ? getParticipantsForAvatarList(participants, actor, entity)
            : undefined;
    // Get filtered reactions if those exist
    const filteredEntryReactions = reactions ? getFilteredReactions(reactions) : undefined;
    // Get comments with filtered reactions if those exist
    const commentsWithFilteredReactions = comments
        ? getCommentsWithFilteredReactions(comments)
        : undefined;
    // Define currentUserId if currentUser exists
    const currentUserId = currentUser ? currentUser.id : undefined;

    const onToggleReaction = currentUser
        ? (
              changeLogId: string,
              reactionType: ReactionType,
              filteredReactions: Reactions,
              parentChangeLogId?: string
          ) => {
              return toggleReaction({
                  changeLogId,
                  reactionType,
                  user: currentUser,
                  toggleAction: getToggleAction(reactionType, filteredReactions, currentUser.id),
                  parentChangeLogId,
              });
          }
        : undefined;

    const isLastEntry =
        Boolean(React.useContext(TimelineEntryContext).lastEntryId === id) || props.isLastEntry;
    const isTimelineSmall =
        Boolean(React.useContext(TimelineEntryContext).isTimelineSmall) || props.isTimelineSmall;

    const leftLineColor = colors[icon.backgroundColor];

    return (
        <div
            style={{marginBottom: isLastEntry ? '60px' : undefined}}
            ref={(ref) => {
                // @ts-expect-error
                timelineEntryRef.current = ref;
            }}
            className={styles['timeline-entry']}
        >
            <div
                className={
                    isTimelineSmall
                        ? styles['entry-header-container--small']
                        : styles['entry-header-container']
                }
            >
                <TimelineEntryIcon
                    icon={icon}
                    gutterContainerRef={timelineEntryRef}
                    isSmall={isTimelineSmall}
                    isLastEntry={isLastEntry}
                    onClickOpen={onClickOpen}
                    hasGutterLine={hasGutterLine}
                />
                <TimelineHeader
                    timestamp={timestamp}
                    actor={actor}
                    entryType={entryType}
                    entity={entity}
                    participants={participants}
                    changeType={changeType}
                    isSmall={isTimelineSmall}
                    pinnedInfo={pinnedInfo}
                    numVisits={numVisits}
                    isPinnedThread={isPinnedThread}
                    threadType={threadType}
                    externalChannelStatus={props.externalChannelStatus}
                />
            </div>
            <div
                className={
                    isTimelineSmall
                        ? styles['timeline-entry-container--small']
                        : styles['timeline-entry-container']
                }
            >
                <TimelineEntryCard
                    changeLogId={id}
                    subject={subject}
                    subjectTitle={subjectTitle}
                    content={content}
                    truncatedContent={truncatedContent}
                    status={status}
                    participants={participantsForAvatarList}
                    relatedLeads={relatedLeads}
                    metadata={metadata}
                    filteredEntryReactions={filteredEntryReactions}
                    comments={commentsWithFilteredReactions}
                    currentUserId={currentUserId}
                    onToggleReaction={onToggleReaction}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    onClickOpen={onClickOpen}
                    customBorderProps={customBorderProps}
                    emailEngagement={emailEngagement}
                    threadData={threadData}
                    disableComments={disableComments}
                    disableReactions={disableReactions}
                    actionMenu={actionMenu}
                    additionalToolbarOptions={additionalToolbarOptions}
                    isExpanded={isExpanded}
                    leftLineColor={leftLineColor as string}
                    externalMeetingTranscriptSummary={externalMeetingTranscriptSummary}
                    hasActivityTranscription={hasActivityTranscription}
                    isDashboard={isDashboard}
                />
            </div>
        </div>
    );
}
